import moment from 'moment-timezone';
import { BasicChartDataSource } from '@app/data/interface/widget-config';

/**
 * Represents the raw response from the environmental data API for a single series.
 */
export type RawSeriesResponse = {
  analytes?: any[];
  data: Array<{ date_time: string; [key: string]: any }>;
};

export type GroupBy =
  | {
      grouping_type: 'time';
      period: 'month' | 'quarter';
    }
  | {
      grouping_type: 'field';
      field: string;
    };

/**
 * Data buckets for aggregation.
 */
type Buckets = Record<string, number[]>;

/**
 * Normalizes the series data by transforming the raw query responses into a consistent format.
 *
 * @param queryResponses Array of raw series responses to normalize
 * @param dataSources Array of data sources corresponding to the series responses
 * @param xAxisData Array of x-axis labels (dates) to align the data with
 * @returns Array of normalized series data
 */
export function normalizeSeriesData(
  queryResponses: RawSeriesResponse[],
  dataSources: BasicChartDataSource[],
  xAxisData: string[]
) {
  const totalsByDate: { [date: string]: number } = {};
  xAxisData.forEach((dateKey) => (totalsByDate[dateKey] = 0));

  queryResponses.forEach((seriesData: RawSeriesResponse, dataSourceIndex: number) => {
    const valueField = dataSources[dataSourceIndex].valueField;
    seriesData.data.forEach((dataPoint) => {
      const value = Number(dataPoint[valueField]);
      const add = isFinite(value) && value > 0 ? value : 0;
      if (dataPoint.date_time && totalsByDate[dataPoint.date_time] !== undefined) {
        totalsByDate[dataPoint.date_time] += add;
      }
    });
  });

  const normalized = queryResponses.map((seriesData: RawSeriesResponse, sidx: number) => {
    const valueField = dataSources[sidx].valueField;
    const keyed: { [date: string]: number | null } = seriesData.data.reduce((dateValueMap: any, dataPoint) => {
      const value = Number(dataPoint[valueField]);
      dateValueMap[dataPoint.date_time] = isFinite(value) ? value : null;
      return dateValueMap;
    }, {});

    return xAxisData.map((dateKey) => {
      const rawVal = keyed[dateKey];
      if (rawVal === null || rawVal === undefined) return null;
      const total = totalsByDate[dateKey] ?? 0;
      if (total <= 0) return 0;
      return rawVal / total;
    });
  });

  return normalized;
}

/**
 * Creates a reducer function for bucketing data points by a given grouping strategy.
 * The returned function is suitable for use with Array.reduce() to accumulate.
 * @param valueField The field containing the value to aggregate.
 * @param groupBy The grouping strategy.
 * @returns A reducer function for bucketing data.
 */
export function createBucketReducer(valueField: string, groupBy: GroupBy) {
  return (buckets: Buckets, dataPoint: any) => {
    const value = Number(dataPoint[valueField]);
    if (!isFinite(value)) {
      return buckets;
    }

    let key: string;
    if (groupBy.grouping_type === 'time') {
      if (!dataPoint?.date_time) {
        return buckets;
      }
      const format = groupBy.period === 'month' ? 'YYYY-MM' : '[Q]Q YYYY';
      key = moment(dataPoint.date_time).format(format);
    } else {
      if (!dataPoint.metadata || dataPoint.metadata[groupBy.field] === undefined) {
        return buckets;
      }
      key = String(dataPoint.metadata[groupBy.field]);
    }

    (buckets[key] ??= []).push(value);
    return buckets;
  };
}

/**
 * Reduces a set of numeric values within a bucket to a single aggregated value.
 *
 * @param key The bucket key (e.g., '2023-01' or a field value)
 * @param buckets Object mapping bucket keys to arrays of numeric values
 * @param valueField The field from which the values were aggregated.
 * @param groupBy The grouping strategy.
 * @returns An aggregated data point for the bucket.
 */
export function reduceBucket(key: string, buckets: Buckets, valueField: string, groupBy: GroupBy) {
  const inputValues = buckets[key];
  let reducedValues: number | null = null;

  const aggregations: Record<string, (values: number[]) => number | null> = {
    sum: (value) => (value.length ? value.reduce((a, b) => a + b, 0) : null),
    avg: (value) => (value.length ? value.reduce((a, b) => a + b, 0) / value.length : null),
    min: (value) => (value.length ? Math.min(...value) : null),
    max: (value) => (value.length ? Math.max(...value) : null),
  };

  reducedValues = (aggregations[valueField] ?? aggregations.avg)((inputValues as number[]) || []);

  let date_time: string;
  if (groupBy.grouping_type === 'time') {
    const format = groupBy.period === 'month' ? 'YYYY-MM' : '[Q]Q YYYY';
    date_time = moment(key, format).startOf(groupBy.period).format('YYYY-MM-DD');
  } else {
    date_time = key;
  }

  return {
    date_time,
    [valueField]: reducedValues,
    avg: reducedValues,
  };
}

/**
 * Group each series by a specified grouping strategy and aggregate values.
 *
 * @param queryResponses Array of RawSeriesResponse objects to group
 * @param dataSources Corresponding array of BasicChartDataSource objects
 * @param groupBy The grouping strategy.
 * @returns New array of RawSeriesResponse objects with aggregated data.
 */
export function groupSeriesResponses(
  queryResponses: RawSeriesResponse[],
  dataSources: BasicChartDataSource[],
  groupBy: GroupBy
) {
  return queryResponses.map((seriesData: RawSeriesResponse, dataSourceIndex: number) => {
    const valueField = dataSources[dataSourceIndex].valueField;
    const reducer = createBucketReducer(valueField, groupBy);
    const buckets = (seriesData.data || []).reduce<Buckets>(reducer, {});

    const aggregatedData = Object.keys(buckets)
      .sort()
      .map((key) => reduceBucket(key, buckets, valueField, groupBy));

    return {
      ...seriesData,
      data: aggregatedData,
    } as RawSeriesResponse;
  });
}

/**
 * Formats a value for display in the chart's x-axis.
 *
 * @param value Value to format
 * @param groupBy The grouping strategy used.
 * @returns Formatted date string or original value if invalid
 */
export function formatXAxisLabel(value: any, groupBy: GroupBy) {
  if (groupBy.grouping_type !== 'time') {
    return value;
  }
  const date = moment(value);
  if (!date.isValid()) {
    return value;
  }
  const format = groupBy.period === 'month' ? 'MMM YY' : '[Q]Q YY';
  return date.format(format);
}
